import * as styles from "./Nav.css";
import {
  ComponentPropsWithRef,
  ComponentType,
  useEffect,
  useRef,
  useState,
} from "react";
import { Contact, Menu, Settings } from "~/sanity/types";

// @ts-ignore
import Phone from "@material-design-icons/svg/outlined/phone.svg?react";
import { NavLogoProps } from "~/assets/svgs/logos/NavLogoConstruction";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { NavLink } from "@remix-run/react";
import { ButtonLink } from "../elements/ButtonLink";
import ScrollTrigger from "gsap/dist/ScrollTrigger";
import { BurgerMenu } from "./BurgerMenu";
import { useLocation } from "react-router-dom";

export type NavProps = {
  data?: Menu;
  NavLogo: ComponentType<NavLogoProps>;
  helpline?: Contact;
  socials?: Settings["socials"];
} & ComponentPropsWithRef<"nav">;

export default function Nav({
  children,
  className,
  data,
  helpline,
  NavLogo,
  socials,
  ...props
}: NavProps) {
  const navRef = useRef<HTMLDivElement | null>(null);
  const navWrapperRef = useRef<HTMLElement | null>(null);
  const navBackgroundColorRef = useRef<HTMLDivElement | null>(null);
  const menuBackgroundColorRef = useRef<HTMLDivElement | null>(null);
  const [inverse, setInverse] = useState(false);
  const [backgroundScaleVisible, setBackgroundScaleVisible] = useState(false);
  const [burgerMenuOpen, setBurgerMenuOpen] = useState(false);
  const [hasHeroSecondary, setHasHeroSecondary] = useState(false);
  const burgerMenuOpenRef = useRef(burgerMenuOpen);
  const lastScrollYRef = useRef(0);
  const location = useLocation();

  burgerMenuOpenRef.current = burgerMenuOpen;

  useEffect(() => {
    const hasHeroSecondary = Boolean(
      document.getElementById("hero-secondary-heading"),
    );
    setHasHeroSecondary(hasHeroSecondary);
  }, [location]);

  function resetMenuState() {
    setBurgerMenuOpen(false);
    setBackgroundScaleVisible(false);
    if (window.innerWidth < 768) setInverse(true);
  }

  // RESET MENU STATE ON WINDOW RESIZE
  useEffect(() => {
    function updateWidth() {
      resetMenuState();
    }
    window.addEventListener("resize", updateWidth);
    updateWidth();
    return () => {
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // RESET MENU STATE ON ROUTE CHANGE
  useEffect(() => {
    resetMenuState();
  }, [location]);

  // Unified navbar state management
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = window.innerHeight * 0.2;
      const isMobile = window.innerWidth < 768;

      // Prevent Safari bounce effect from triggering scroll logic
      if (scrollY < 0) {
        return;
      }

      // Determine scroll direction
      const isScrollingDown = scrollY > lastScrollYRef.current;
      lastScrollYRef.current = scrollY;

      const shouldBeInverse = isMobile || scrollY > threshold || burgerMenuOpen;

      if (!isMobile) {
        setInverse(shouldBeInverse);
      }

      // Animate background scale when scrolling down and navbar shouldn't be inverse
      if (scrollY < threshold) {
        if (isScrollingDown && !shouldBeInverse) {
          setBackgroundScaleVisible(true);

          if (!isMobile) {
            setInverse(true);
          }
        } else if (!isScrollingDown) {
          setBackgroundScaleVisible(false);
          if (!isMobile && !burgerMenuOpen) {
            setInverse(false);
          }
        }
      } else {
        setBackgroundScaleVisible(true);
      }
    };

    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [burgerMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = burgerMenuOpen ? "hidden" : "auto";
  }, [burgerMenuOpen]);

  // Keep header visible while burger menu is open (ScrollTrigger may not fire without scroll)
  useEffect(() => {
    if (!burgerMenuOpen || !navWrapperRef.current) return;
    gsap.to(navWrapperRef.current, { yPercent: 0, duration: 0.3 });
  }, [burgerMenuOpen]);

  // SLIDE NAV IN OR OUT BASED ON SCROLL DIRECTION
  useGSAP(() => {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.refresh();

    const navTl = gsap.timeline({
      defaults: {},
      scrollTrigger: {
        trigger: navRef.current,
        fastScrollEnd: 3000,
        start: `top top-=20%`,
        end: () =>
          `${document.documentElement.scrollHeight - window.innerHeight} top`,
        toggleActions: "play none none reverse",

        onUpdate: (self) => {
          if (burgerMenuOpenRef.current) {
            gsap.to(navWrapperRef.current, {
              yPercent: 0,
              duration: 0.3,
            });
            return;
          }
          !(self.direction >= 0)
            ? gsap.to(navWrapperRef.current, {
                yPercent: 0,
                duration: 0.3,
              })
            : gsap.to(navWrapperRef.current, {
                yPercent: -110,
                duration: 0.3,
              });
        },
      },
    });

    return () => {
      navTl.kill();
      navTl.scrollTrigger?.kill();
    };
  }, {});

  return (
    <>
      <header ref={navWrapperRef} className={styles.menuWrapper} {...props}>
        <nav className={styles.nav} ref={navRef}>
          <div
            className={styles.backgroundColor({
              inverse: backgroundScaleVisible,
            })}
            ref={navBackgroundColorRef}
          />
          <div className={styles.navContent} ref={menuBackgroundColorRef}>
            <NavLink to="/" className={styles.logo}>
              <NavLogo
                inverted={hasHeroSecondary ? true : inverse || burgerMenuOpen}
                solid={hasHeroSecondary && !burgerMenuOpen ? true : false}
              />
            </NavLink>

            <div className={styles.navButtonsContainer}>
              <div className={styles.navButtons}>
                {helpline && (
                  <>
                    <div className={styles.hide({ showOn: "desktop" })}>
                      <ButtonLink
                        link={helpline.link}
                        icons={{ iconPrimary: <Phone /> }}
                        theme="outline"
                      >
                        {helpline.link?.text || "Help Line"}
                      </ButtonLink>
                    </div>
                    <div className={styles.hide({ showOn: "mobile" })}>
                      <ButtonLink
                        link={helpline.link}
                        icons={{ iconPrimary: <Phone /> }}
                        theme="colour"
                      ></ButtonLink>
                    </div>
                  </>
                )}

                {/* BURGER MENU COMPONENT HERE */}
                {data && (
                  <div className={styles.burgerMenu}>
                    <BurgerMenu
                      data={data}
                      setIsBurgerMenuOpen={setBurgerMenuOpen}
                      socials={socials}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
}
